import { useEffect, useState } from 'react';
import type { PresenceSnapshot } from '../../server/presence.js';
import type { BrowserProfile } from './profile.js';

const HEARTBEAT_INTERVAL_MS = 10_000;

export const EMPTY_PRESENCE: PresenceSnapshot = {
  onlineCount: 0,
  searchingCount: 0,
  searchers: [],
  serverTime: 0,
};

export interface PresenceState {
  snapshot: PresenceSnapshot;
  loading: boolean;
  error?: string;
}

export type PresenceProfileSource = 'local' | 'account';

export async function sendPresenceHeartbeat(
  profile: BrowserProfile,
  searching: boolean,
  signal?: AbortSignal,
  profileSource: PresenceProfileSource = 'local',
): Promise<PresenceSnapshot> {
  const heartbeat = profileSource === 'account'
    ? { presenceId: profile.presenceId, searching }
    : {
        profileId: profile.profileId,
        presenceId: profile.presenceId,
        nickname: profile.nickname,
        searching,
      };
  const response = await fetch('/api/presence', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(heartbeat),
    ...(signal !== undefined ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`сервер присутствия вернул ${response.status}`);
  return parsePresenceSnapshot(await response.json() as unknown);
}

export function usePresence(
  profile: BrowserProfile | undefined,
  searching: boolean,
  profileSource: PresenceProfileSource = 'local',
): PresenceState {
  const [state, setState] = useState<PresenceState>({
    snapshot: EMPTY_PRESENCE,
    loading: profile !== undefined,
  });

  useEffect(() => {
    if (profile === undefined) return undefined;
    let active = true;
    const controller = new AbortController();
    const pulse = async (): Promise<void> => {
      try {
        const snapshot = await sendPresenceHeartbeat(
          profile,
          searching,
          controller.signal,
          profileSource,
        );
        if (active) setState({ snapshot, loading: false });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          loading: false,
          error: error instanceof Error ? error.message : 'статистика временно недоступна',
        }));
      }
    };
    void pulse();
    const timer = setInterval(() => void pulse(), HEARTBEAT_INTERVAL_MS);
    return () => {
      active = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [profile?.profileId, profile?.presenceId, profile?.nickname, profileSource, searching]);

  return state;
}

function parsePresenceSnapshot(value: unknown): PresenceSnapshot {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('сервер вернул некорректную статистику');
  }
  const record = value as Partial<PresenceSnapshot>;
  if (!Number.isSafeInteger(record.onlineCount) || (record.onlineCount ?? -1) < 0
    || !Number.isSafeInteger(record.searchingCount) || (record.searchingCount ?? -1) < 0
    || !Number.isFinite(record.serverTime) || !Array.isArray(record.searchers)) {
    throw new Error('сервер вернул некорректную статистику');
  }
  const searchers = record.searchers.map((searcher) => {
    if (typeof searcher !== 'object' || searcher === null
      || typeof searcher.nickname !== 'string' || !Number.isFinite(searcher.searchingSince)) {
      throw new Error('сервер вернул некорректный список игроков');
    }
    return { nickname: searcher.nickname, searchingSince: searcher.searchingSince };
  });
  return {
    onlineCount: record.onlineCount!,
    searchingCount: record.searchingCount!,
    searchers,
    serverTime: record.serverTime!,
  };
}
